// Every diagram and chart on the site lives here. Edit the words freely; Figure.js does the drawing.
//
// A figure is one card: title, one-sentence caption, a text version (alt) and one drawing:
//   flow   boxes and arrows on a grid
//   bar    one horizontal bar split into labelled segments
//   chart  grouped horizontal bars
//
// In a flow, each node sits on a grid. `at` is [column, row, columns spanned] on a wide card and
// `atN` is the same on a narrow one (a phone). Columns may be fractions; rows are whole numbers.
//   kind  step (default), decision (diamond), db (cylinder) or end (a result)
//   tone  emotion, gap, difficulty, generator, analytics, or leave it out for shared parts
// A group draws a labelled frame around its members (foot: true puts the label underneath).
// An edge is [from, to, options], and may start or end at a group. Options: label, dashed, shift (pixels sideways), and via, which
// picks the bend for arrows that are not in line: 'vhv' (default), 'hvh', 'vh', 'hv' or 'u' (loop back).
// A pair such as via: ['vh', 'hv'] means wide first, narrow second.
import { evaluation, nextActions, readinessFormula, sessionFlow } from './content';

const sessionLoop = {
  title: 'The closed loop, one answer at a time',
  caption: 'Every answer passes through all five components before the next question is chosen.',
  alt: [...sessionFlow.map(([step]) => `${step}.`), 'The loop then repeats with the next question.'],
  flow: {
    cols: [3, 2],
    wideMin: 520,
    nodes: [
      { id: 'answer', label: sessionFlow[0][0], sub: 'Learner', at: [0, 0], atN: [0, 0] },
      { id: 'emotion', label: sessionFlow[1][0], sub: 'Emotion', tone: 'emotion', at: [1, 0], atN: [0, 1] },
      { id: 'diagnose', label: sessionFlow[2][0], sub: 'Knowledge gaps', tone: 'gap', at: [2, 0], atN: [0, 2] },
      { id: 'predict', label: sessionFlow[3][0], sub: 'Difficulty', tone: 'difficulty', at: [2, 1], atN: [1, 2] },
      { id: 'serve', label: sessionFlow[4][0], sub: 'Exercises', tone: 'generator', at: [1, 1], atN: [1, 1] },
      { id: 'update', label: sessionFlow[5][0], sub: 'Analytics', tone: 'analytics', at: [0, 1], atN: [1, 0] },
    ],
    edges: [
      ['answer', 'emotion'], ['emotion', 'diagnose'], ['diagnose', 'predict'],
      ['predict', 'serve'], ['serve', 'update'], ['update', 'answer'],
    ],
  },
};

const services = [
  ['emo', 'Emotion service', 'Node.js, Express, face-api.js', 'Emotion database', 'emotion'],
  ['dif', 'Difficulty prediction', 'Python, Flask, scikit-learn', 'Difficulty database', 'difficulty'],
  ['kg', 'Knowledge-gap localization', 'Python, Flask', 'Knowledge-gap database', 'gap'],
  ['gen', 'Exercise generator', 'Node.js, Express, OpenAI API', 'Exercise database', 'generator'],
  ['ana', 'Learning analytics', 'Node.js, Express, node-cron', 'Analytics database', 'analytics'],
  ['login', 'User login', 'Node.js, Express, Google OAuth', 'Login database'],
];

const architecture = {
  title: 'System architecture',
  caption: 'Requests go from the React frontend through one gateway to six services, and every part runs in its own container under one Docker Compose definition.',
  alt: [
    'The React frontend, served by nginx, talks only to the API gateway.',
    'The gateway (Node.js, Express) routes each request to one of six services: emotion, difficulty prediction, knowledge-gap localization, exercise generator, learning analytics and user login.',
    'Each service owns one logical database inside a single MongoDB 7 instance.',
    'Learning analytics also reads every other database, without write access.',
    'Every part runs in its own container, orchestrated by one Docker Compose definition.',
  ],
  flow: {
    cols: [6, 2],
    dashed: 'Read-only: learning analytics reads every database',
    nodes: [
      { id: 'fe', label: 'Frontend', sub: 'React, nginx', at: [2.5, 0], atN: [0, 0] },
      { id: 'gw', label: 'API gateway', sub: 'Node.js, Express', at: [2.5, 1], atN: [0, 1] },
      ...services.map(([id, label, sub, , tone], i) => ({ id, label, sub, tone, at: [i, 2], atN: [0, 2 + i] })),
      ...services.map(([id, , , label, tone], i) => ({ id: `${id}-db`, kind: 'db', label, tone, at: [i, 3], atN: [1, 2 + i] })),
    ],
    groups: [
      { id: 'services', label: 'Services', members: services.map(([id]) => id) },
      { id: 'mongo', label: 'MongoDB 7, one logical database per component', foot: true, members: services.map(([id]) => `${id}-db`) },
    ],
    edges: [
      ['fe', 'gw'], ['gw', 'services'],
      ...services.map(([id]) => [id, `${id}-db`]),
      ['ana', 'mongo', { dashed: true, label: 'read-only', shift: 30 }],
    ],
  },
};

const emotionPipeline = {
  title: 'From webcam frame to support',
  caption: 'A frame becomes a tip only when a confident, negative emotion lasts for five seconds.',
  alt: [
    'A webcam frame is captured every 250 ms.',
    'A face is detected in the frame. If the learner looks away for 8 s, an attention nudge is shown.',
    'The expression scores are calibrated and then smoothed.',
    'Predictions under 55% confidence are ignored.',
    'A negative emotion held for 5 s triggers a tip or a motivational message.',
    'Neutral and happy trigger no action.',
  ],
  flow: {
    cols: [5, 2],
    wideMin: 700,
    nodes: [
      { id: 'cam', label: 'Webcam frame', sub: 'every 250 ms', tone: 'emotion', at: [0, 0], atN: [0, 0, 2] },
      { id: 'face', label: 'Face detection', tone: 'emotion', at: [1, 0], atN: [0, 1, 2] },
      { id: 'cal', label: 'Calibration', tone: 'emotion', at: [2, 0], atN: [0, 2] },
      { id: 'smooth', label: 'Smoothing', tone: 'emotion', at: [3, 0], atN: [0, 3] },
      { id: 'conf', label: 'Confidence check', sub: 'at least 55%', tone: 'emotion', at: [4, 0], atN: [0, 4] },
      { id: 'neg', kind: 'decision', label: 'Negative emotion held for 5\u00a0s?', tone: 'emotion', at: [4, 1], atN: [0, 5, 2] },
      { id: 'tip', kind: 'end', label: 'Tip or motivational message', tone: 'emotion', at: [3, 1], atN: [0, 6] },
      { id: 'none', kind: 'end', label: 'No action', sub: 'neutral or happy', tone: 'emotion', at: [4, 2], atN: [1, 6] },
      { id: 'nudge', kind: 'end', label: 'Attention nudge', tone: 'emotion', at: [1, 1], atN: [1, 2] },
    ],
    edges: [
      ['cam', 'face'], ['face', 'cal'], ['cal', 'smooth'], ['smooth', 'conf'], ['conf', 'neg'],
      ['neg', 'tip', { label: 'yes' }], ['neg', 'none', { label: 'no' }],
      ['face', 'nudge', { label: 'looking away for 8\u00a0s' }],
    ],
  },
};

const mistakeTree = {
  title: 'Why was the answer wrong?',
  caption: 'After a wrong answer, these questions are asked in order and the first “yes” names the probable cause.',
  alt: [
    'Answered in under 5 s on the first attempt: a careless mistake if topic accuracy is 0.6 or more, rushed if it is below 0.6.',
    'Otherwise, negative emotion with 3 or more attempts or wrong answers: frustrated or stuck.',
    'Otherwise, negative emotion with 2 or fewer attempts and over 20 s: low-confidence hesitation.',
    'Otherwise, a specific error pattern: conceptual misunderstanding.',
    'Otherwise: knowledge gap.',
  ],
  flow: {
    cols: [6, 2],
    wideMin: 800,
    nodes: [
      { id: 'q1', kind: 'decision', label: 'Under 5\u00a0s on the first attempt?', tone: 'gap', at: [0, 0, 2], atN: [0, 0, 2] },
      { id: 'careless', kind: 'end', label: 'Careless mistake', sub: 'topic accuracy 0.6 or more', tone: 'gap', at: [0, 1], atN: [0, 1] },
      { id: 'rushed', kind: 'end', label: 'Rushed', sub: 'topic accuracy below 0.6', tone: 'gap', at: [1, 1], atN: [1, 1] },
      { id: 'q2', kind: 'decision', label: 'Negative emotion and 3 or more attempts or wrong answers?', tone: 'gap', at: [2, 0, 2], atN: [0, 2, 2] },
      { id: 'stuck', kind: 'end', label: 'Frustrated or stuck', tone: 'gap', at: [3, 1], atN: [1, 3] },
      { id: 'q3', kind: 'decision', label: 'Negative emotion, 2 or fewer attempts and over 20\u00a0s?', tone: 'gap', at: [4, 0, 2], atN: [0, 4, 2] },
      { id: 'hesitant', kind: 'end', label: 'Low-confidence hesitation', tone: 'gap', at: [5, 1], atN: [1, 5] },
      { id: 'q4', kind: 'decision', label: 'A specific error pattern?', tone: 'gap', at: [2, 2, 2], atN: [0, 6, 2] },
      { id: 'kgap', kind: 'end', label: 'Knowledge gap', tone: 'gap', at: [1.5, 3, 1.5], atN: [0, 7] },
      { id: 'concept', kind: 'end', label: 'Conceptual misunder\u00adstanding', tone: 'gap', at: [3, 3, 1.5], atN: [1, 7] },
    ],
    edges: [
      ['q1', 'careless', { label: 'yes' }], ['q1', 'rushed', { label: 'yes' }], ['q1', 'q2', { label: 'no' }],
      ['q2', 'stuck', { label: 'yes' }], ['q2', 'q3', { label: 'no' }],
      ['q3', 'hesitant', { label: 'yes' }], ['q3', 'q4', { label: 'no' }],
      ['q4', 'concept', { label: 'yes' }], ['q4', 'kgap', { label: 'no' }],
    ],
  },
};

const difficultyPipeline = {
  title: 'From one answer to the next difficulty',
  caption: 'Three scores are built from the answer, and a Random Forest turns them into Easy, Medium or Hard.',
  alt: [
    'The answer, time, attempts and QDI make a composite score from 0 to 10.',
    'Cognitive effort is worked out from time, attempts and answer quality.',
    'Learner readiness combines the score, the effort, recent accuracy and the learning trend.',
    'A Random Forest with 17 features predicts Easy, Medium or Hard for the next question.',
    'Guardrail: a fully incorrect answer can never move the learner to a harder band.',
    'Fallback: if the model is unavailable, score bands choose the difficulty instead.',
  ],
  flow: {
    cols: [6, 2],
    dashed: 'Fallback path, used only if the model is unavailable',
    nodes: [
      { id: 'in', label: 'Answer, time, attempts and QDI', at: [0, 0], atN: [0, 0] },
      { id: 'score', label: 'Composite score', sub: '0 to 10', tone: 'difficulty', at: [1, 0], atN: [0, 1] },
      { id: 'effort', label: 'Cognitive effort', tone: 'difficulty', at: [2, 0], atN: [0, 2] },
      { id: 'ready', label: 'Learner readiness', tone: 'difficulty', at: [3, 0], atN: [0, 3] },
      { id: 'forest', label: 'Random Forest', sub: '17 features', tone: 'difficulty', at: [4, 0], atN: [0, 4] },
      { id: 'guard', label: 'Guardrail', sub: 'a wrong answer cannot step up', tone: 'difficulty', at: [5, 0], atN: [0, 5] },
      { id: 'out', kind: 'end', label: 'Easy, Medium or Hard', tone: 'difficulty', at: [5, 1], atN: [0, 6] },
      { id: 'bands', label: 'Score-band fallback', sub: 'model unavailable', tone: 'difficulty', at: [4, 1], atN: [1, 5] },
    ],
    edges: [
      ['in', 'score'], ['score', 'effort'], ['effort', 'ready'], ['ready', 'forest'], ['forest', 'guard'], ['guard', 'out'],
      ['score', 'bands', { dashed: true, via: ['vh', 'hv'] }],
      ['bands', 'out', { dashed: true, via: 'vh' }],
    ],
  },
};

const dvLifecycle = {
  title: 'The life of a question',
  caption: 'A question’s difficulty value (DV) comes from how learners actually found it, and a question that proves too hard is replaced.',
  alt: [
    'The exercise generator writes a question.',
    'It is displayed to learners, and some of them mark it hard.',
    'After 5 displays, DV = times marked hard ÷ times displayed.',
    'The question is sorted into Easy (DV up to 0.3), Medium (up to 0.7) or Hard (above 0.7).',
    'At DV 0.9 or above it is replaced and the bucket is refilled.',
    'Each tip view from the emotion component adds 0.01 to the question’s DV.',
  ],
  flow: {
    cols: [6, 2],
    nodes: [
      { id: 'tip', label: 'Tip viewed', sub: 'adds 0.01 to\u00a0DV', tone: 'emotion', at: [3, 0], atN: [1, 3] },
      { id: 'made', label: 'Question generated', tone: 'generator', at: [0, 1], atN: [0, 0] },
      { id: 'shown', label: 'Displayed to learners', tone: 'generator', at: [1, 1], atN: [0, 1] },
      { id: 'hard', label: 'Marked hard', tone: 'generator', at: [2, 1], atN: [0, 2] },
      { id: 'dv', label: 'DV recalculated', sub: 'marked hard ÷ displayed, after 5 displays', tone: 'generator', at: [3, 1], atN: [0, 3] },
      { id: 'sort', label: 'Sorted into a bucket', sub: 'Easy to 0.3, Medium to 0.7, Hard above', tone: 'generator', at: [4, 1], atN: [0, 4] },
      { id: 'swap', label: 'Replaced, bucket refilled', sub: 'at DV 0.9 or above', tone: 'generator', at: [5, 1], atN: [0, 5] },
    ],
    edges: [
      ['made', 'shown'], ['shown', 'hard'], ['hard', 'dv'], ['dv', 'sort'], ['sort', 'swap'],
      ['swap', 'made', { via: 'u', label: 'a new question is generated' }],
      ['tip', 'dv'],
    ],
  },
};

const pathway = {
  title: 'Pathway stage and next action',
  caption: 'The profile is turned into one of four stages, and a priority list picks the single next action.',
  alt: [
    'Fewer than 10 attempts: the stage is foundation.',
    'Otherwise the stage starts from the schema level.',
    'The overall score can then move it up or down.',
    'Emotional stability below 40 caps it one stage lower.',
    'The result is building, consolidating or advanced.',
  ],
  flow: {
    cols: [2, 2],
    wideMin: Infinity,
    nodes: [
      { id: 'few', kind: 'decision', label: 'Fewer than 10 attempts?', tone: 'analytics', atN: [0, 0, 2] },
      { id: 'schema', label: 'Start from the schema level', tone: 'analytics', atN: [0, 1] },
      { id: 'base', kind: 'end', label: 'Foundation', tone: 'analytics', atN: [1, 1] },
      { id: 'refine', label: 'Refine by the overall score', tone: 'analytics', atN: [0, 2] },
      { id: 'cap', label: 'Cap if emotional stability is below 40', tone: 'analytics', atN: [0, 3] },
      { id: 'stage', kind: 'end', label: 'Building, consolidating or advanced', tone: 'analytics', atN: [0, 4] },
    ],
    edges: [
      ['few', 'schema', { label: 'no' }], ['few', 'base', { label: 'yes' }],
      ['schema', 'refine'], ['refine', 'cap'], ['cap', 'stage'],
    ],
  },
  listTitle: 'Next action, first match wins',
  list: nextActions.map(([, condition, action]) => [action, condition]),
};

const readinessBands = {
  title: 'Learner readiness bands',
  caption: 'Readiness runs from 0 to 1 and falls into one of three bands.',
  alt: 'Below 0.4 is low, from 0.4 up to 0.7 is moderate, and 0.7 or more is high.',
  bar: {
    segments: [{ size: 0.4, label: 'Low' }, { size: 0.3, label: 'Moderate' }, { size: 0.3, label: 'High' }],
    ticks: [0, 0.4, 0.7, 1],
  },
  expr: readinessFormula,
};

const effortWeights = {
  title: 'What cognitive effort is made of',
  caption: 'Time counts for 0.4 of cognitive effort, and attempts and answer quality for 0.3 each.',
  alt: 'Cognitive effort = 0.4 × time effort + 0.3 × attempt effort + 0.3 × answer-quality effort.',
  bar: {
    segments: [
      { size: 0.4, label: 'Time effort', value: '0.4' },
      { size: 0.3, label: 'Attempt effort', value: '0.3' },
      { size: 0.3, label: 'Answer quality', value: '0.3' },
    ],
  },
};

const scoreWeights = {
  title: 'What the overall learning score is made of',
  caption: 'Accuracy counts for 0.4 of the overall score, and the knowledge-gap and confidence parts for 0.3 each.',
  alt: 'Score = 0.4 × Accuracy + 0.3 × (100 − KnowledgeGap) + 0.3 × Confidence.',
  bar: {
    segments: [
      { size: 0.4, label: 'Accuracy', value: '0.4' },
      { size: 0.3, label: '100 − Knowledge gap', value: '0.3' },
      { size: 0.3, label: 'Confidence', value: '0.3' },
    ],
  },
};

const dvScale = {
  title: 'The DV scale',
  caption: 'A question’s DV runs from 0 to 1 and decides which bucket it sits in.',
  alt: 'Up to 0.3 is Easy, up to 0.7 is Medium and above 0.7 is Hard. A question at 0.9 or above is replaced.',
  bar: {
    segments: [{ size: 0.3, label: 'Easy' }, { size: 0.4, label: 'Medium' }, { size: 0.3, label: 'Hard' }],
    ticks: [0, 0.3, 0.7, 0.9, 1],
    mark: { at: 0.9, label: 'Replaced at 0.9 or above' },
  },
};

// "CNN from scratch, 35.10%" becomes a named bar with a value.
const modelBar = (text, kind) => {
  const cut = text.lastIndexOf(', ');
  return { kind, name: text.slice(0, cut), shown: text.slice(cut + 2), value: parseFloat(text.slice(cut + 2)) };
};

const evaluationChart = {
  title: 'Early results, mostly on synthetic data',
  caption: 'Test accuracy of the compared and the selected model for each component; the difficulty and knowledge-gap figures come from synthetic, rule-labelled data, not real learners.',
  alt: evaluation.rows.map(([name, data, other, chosen]) => `${name}, tested on ${data.toLowerCase()}: ${other} (compared) and ${chosen} (selected).`),
  chart: {
    max: 100,
    rows: evaluation.rows.map(([name, data, other, chosen]) => ({
      name, note: `Tested on: ${data.toLowerCase()}`, bars: [modelBar(other, 'Compared'), modelBar(chosen, 'Selected')],
    })),
  },
};

// Where each figure appears: a methodology group id, or the architecture and evaluation subsections.
export const figures = {
  'm-overview': [sessionLoop],
  'm-emotion': [emotionPipeline],
  'm-gaps': [mistakeTree],
  'm-difficulty': [difficultyPipeline, readinessBands, effortWeights],
  'm-exercises': [dvLifecycle, dvScale],
  'm-analytics': [pathway, scoreWeights],
  architecture: [architecture],
  evaluation: [evaluationChart],
};
